import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Label} from '@/components/ui/label'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Switch} from '@/components/ui/switch'
import {Checkbox} from '@/components/ui/checkbox'
import {TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {GroupHeader} from '@/components/help-icon'
import {Hint} from '@/components/ui/hint'
import {ControlDimensionEditor} from '@/components/ipevr/control-combobox'
import {MedidasSection} from '@/components/ipevr/medidas-section'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {
  CLASIFICACIONES,
  CONTROL_TIPOS,
  ND_BY_VALOR,
  PROCESOS,
  type Fila,
  type Matriz,
  type RiesgoFilaClasificacion,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {tope} from '@/lib/campos-texto'

const SELECT_CLASS = 'h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground'

/** Same tabs as the details dialog, so a field lives on the same tab when adding, editing or reading. */
export type FilaFormTab = 'contexto' | 'peligro' | 'controles' | 'evaluacion'

export type FilaFormFieldKey =
  | 'proceso'
  | 'zona'
  | 'actividad'
  | 'tarea'
  | 'rutinaria'
  | 'clasificacion'
  | 'descripcion'
  | 'efectos_posibles'
  | 'area_id'
  | 'peor_consecuencia'
  | 'requisito_legal'
  | 'nd'
  | 'ne'
  | 'nc'
  | 'num_expuestos'

export const FIELD_TAB: Record<FilaFormFieldKey, FilaFormTab> = {
  proceso: 'contexto',
  zona: 'contexto',
  actividad: 'contexto',
  tarea: 'contexto',
  rutinaria: 'contexto',
  clasificacion: 'peligro',
  descripcion: 'peligro',
  efectos_posibles: 'peligro',
  area_id: 'controles',
  peor_consecuencia: 'controles',
  requisito_legal: 'controles',
  nd: 'evaluacion',
  ne: 'evaluacion',
  nc: 'evaluacion',
  num_expuestos: 'evaluacion',
}

export type FilaFormState = {
  proceso: string
  zona: string
  actividad: string
  tarea: string
  rutinaria: boolean
  clasificacion: RiesgoFilaClasificacion
  descripcion: string
  efectos_posibles: string
  nd: string
  ne: string
  nc: string
  area_id: string
  num_expuestos: string
  peor_consecuencia: string
  requisito_legal: boolean
}

export const EMPTY_FILA_FORM: FilaFormState = {
  proceso: '',
  zona: '',
  actividad: '',
  tarea: '',
  rutinaria: true,
  clasificacion: 'condiciones_de_seguridad',
  descripcion: '',
  efectos_posibles: '',
  nd: '',
  ne: '',
  nc: '',
  area_id: '',
  num_expuestos: '',
  peor_consecuencia: '',
  requisito_legal: false,
}

export type FilaFormStringField = Exclude<keyof FilaFormState, 'rutinaria' | 'requisito_legal'>

export function makeFilaFormFieldSetter(setForm: (updater: (f: FilaFormState) => FilaFormState) => void) {
  return function set(field: FilaFormStringField) {
    return (e: {target: {value: string}}) => setForm((f) => ({...f, [field]: e.target.value}))
  }
}

export function filaFormToPayload(form: FilaFormState) {
  return {
    proceso: form.proceso || null,
    zona: form.zona || null,
    actividad: form.actividad || null,
    tarea: form.tarea || null,
    rutinaria: form.rutinaria,
    clasificacion: form.clasificacion,
    descripcion: form.descripcion,
    efectos_posibles: form.efectos_posibles || null,
    nd: form.nd ? ND_BY_VALOR[form.nd] : null,
    ne: form.ne ? Number(form.ne) : null,
    nc: form.nc ? Number(form.nc) : null,
    area_id: form.area_id || null,
    num_expuestos: form.num_expuestos ? Number(form.num_expuestos) : null,
    peor_consecuencia: form.peor_consecuencia || null,
    requisito_legal: form.requisito_legal,
  }
}

type Gtc45Option = {key: string; label: string}

export function gtc45Options(matriz: Matriz): {nd: Gtc45Option[]; ne: Gtc45Option[]; nc: Gtc45Option[]} {
  return {
    nd: matriz.gtc45.nd.map((o) => ({
      key: o.valor === null ? 'bajo' : String(o.valor),
      label: `${o.etiqueta_corta} — ${o.linea_llana}`,
    })),
    ne: matriz.gtc45.ne.map((o) => ({key: String(o.valor), label: `${o.etiqueta_corta} — ${o.linea_llana}`})),
    nc: matriz.gtc45.nc.map((o) => ({key: String(o.valor), label: `${o.etiqueta_corta} — ${o.linea_llana}`})),
  }
}

function FormTabTrigger({
  value,
  label,
  testId,
  hasError,
}: {
  value: FilaFormTab
  label: string
  testId: string
  hasError: boolean
}) {
  return (
    <TabsTrigger value={value} data-testid={testId} className="relative">
      {label}
      {hasError && (
        <span
          className="absolute -top-0.5 -right-2 h-1.5 w-1.5 rounded-full bg-destructive"
          data-testid={`${testId}-error-dot`}
          aria-hidden="true"
        />
      )}
    </TabsTrigger>
  )
}

export function FilaFormTabsList({testId, tabsWithErrors}: {testId: string; tabsWithErrors: Set<FilaFormTab>}) {
  const {t} = useTranslation()
  return (
    <TabsList data-testid={testId}>
      <FormTabTrigger
        value="contexto"
        label={t('ipevr.details.tabs.contexto')}
        testId={`${testId}-contexto`}
        hasError={tabsWithErrors.has('contexto')}
      />
      <FormTabTrigger
        value="peligro"
        label={t('ipevr.details.tabs.peligro')}
        testId={`${testId}-peligro`}
        hasError={tabsWithErrors.has('peligro')}
      />
      <FormTabTrigger
        value="controles"
        label={t('ipevr.details.tabs.controles')}
        testId={`${testId}-controles`}
        hasError={tabsWithErrors.has('controles')}
      />
      <FormTabTrigger
        value="evaluacion"
        label={t('ipevr.details.tabs.evaluacion')}
        testId={`${testId}-evaluacion`}
        hasError={tabsWithErrors.has('evaluacion')}
      />
    </TabsList>
  )
}

export function ContextoTabContent({
  form,
  set,
  setForm,
  idPrefix,
  testIdPrefix,
}: {
  form: FilaFormState
  set: (field: 'zona' | 'actividad' | 'tarea') => (e: {target: {value: string}}) => void
  setForm: (updater: (f: FilaFormState) => FilaFormState) => void
  idPrefix: string
  testIdPrefix: string
}) {
  const {t} = useTranslation()
  // A legacy free-text proceso renders as its own option so the select never hides the stored value.
  const procesoOptions =
    PROCESOS.includes(form.proceso as (typeof PROCESOS)[number]) || !form.proceso
      ? PROCESOS
      : [form.proceso, ...PROCESOS]
  return (
    <TabsContent value="contexto" className="space-y-5">
      <GroupHeader
        label={t('ipevr.groups.contexto')}
        help={t('ipevr.groups.contextoHelp')}
        testId={`${testIdPrefix}-help-contexto`}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-proceso`}>{t('ipevr.grid.proceso')}</Label>
          <select
            id={`${idPrefix}-proceso`}
            value={form.proceso}
            onChange={(e) => setForm((f) => ({...f, proceso: e.target.value}))}
            className={SELECT_CLASS}
            data-testid={`${testIdPrefix}-proceso`}>
            <option value="">{t('ipevr.grid.selectPlaceholder')}</option>
            {procesoOptions.map((p) => (
              <option key={p} value={p}>
                {PROCESOS.includes(p as (typeof PROCESOS)[number]) ? t(`ipevr.proceso.${p}`) : p}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-zona`}>{t('ipevr.grid.zona')}</Label>
          <Input
            maxLength={tope('NOTA')}
            id={`${idPrefix}-zona`}
            value={form.zona}
            onChange={set('zona')}
            data-testid={`${testIdPrefix}-zona`}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-actividad`}>{t('ipevr.grid.actividad')}</Label>
          <Input
            maxLength={tope('ORG')}
            id={`${idPrefix}-actividad`}
            value={form.actividad}
            onChange={set('actividad')}
            data-testid={`${testIdPrefix}-actividad`}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-tarea`}>{t('ipevr.grid.tarea')}</Label>
          <Input
            maxLength={tope('ORG')}
            id={`${idPrefix}-tarea`}
            value={form.tarea}
            onChange={set('tarea')}
            data-testid={`${testIdPrefix}-tarea`}
          />
        </div>
      </div>
      <label className="flex min-h-11 items-center gap-2.5 text-sm text-foreground" htmlFor={`${idPrefix}-rutinaria`}>
        <Switch
          id={`${idPrefix}-rutinaria`}
          checked={form.rutinaria}
          onCheckedChange={(checked) => setForm((f) => ({...f, rutinaria: checked}))}
          data-testid={`${testIdPrefix}-rutinaria`}
        />
        {t('ipevr.grid.rutinaria')}
      </label>
    </TabsContent>
  )
}

export function PeligroTabContent({
  form,
  set,
  setForm,
  idPrefix,
  testIdPrefix,
  descripcionError,
}: {
  form: FilaFormState
  set: (field: 'descripcion' | 'efectos_posibles') => (e: {target: {value: string}}) => void
  setForm: (updater: (f: FilaFormState) => FilaFormState) => void
  idPrefix: string
  testIdPrefix: string
  descripcionError?: boolean
}) {
  const {t} = useTranslation()
  return (
    <TabsContent value="peligro" className="space-y-5">
      <GroupHeader
        label={t('ipevr.groups.peligro')}
        help={t('ipevr.groups.peligroHelp')}
        testId={`${testIdPrefix}-help-peligro`}
      />
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-clasificacion`}>{t('ipevr.grid.clasificacion')}</Label>
          <select
            id={`${idPrefix}-clasificacion`}
            value={form.clasificacion}
            onChange={(e) => setForm((f) => ({...f, clasificacion: e.target.value as RiesgoFilaClasificacion}))}
            className={SELECT_CLASS}
            data-testid={`${testIdPrefix}-clasificacion`}>
            {CLASIFICACIONES.map((c) => (
              <option key={c} value={c}>
                {t(`ipevr.clasificacion.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-descripcion`}>{t('ipevr.grid.descripcion')}</Label>
          <Textarea
            maxLength={tope('TEXTO')}
            id={`${idPrefix}-descripcion`}
            minRows={3}
            value={form.descripcion}
            onChange={set('descripcion')}
            data-testid={`${testIdPrefix}-descripcion`}
            aria-invalid={descripcionError}
          />
          {descripcionError && (
            <p className="text-xs text-destructive" data-testid={`${testIdPrefix}-descripcion-error`}>
              {t('ipevr.errors.descripcionRequired')}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-efectos`}>{t('ipevr.grid.efectosPosibles')}</Label>
          <Textarea
            maxLength={tope('TEXTO')}
            id={`${idPrefix}-efectos`}
            minRows={3}
            value={form.efectos_posibles}
            onChange={set('efectos_posibles')}
            placeholder={t('ipevr.grid.efectosPosiblesPlaceholder')}
            data-testid={`${testIdPrefix}-efectos`}
          />
        </div>
      </div>
    </TabsContent>
  )
}

/** Controles and medidas are the fila's own sub-resources and save immediately, apart from
 * the form's Guardar; a row not yet created (no `fila`) only gets a note. */
export function ControlesTabContent({
  form,
  set,
  setForm,
  idPrefix,
  testIdPrefix,
  matrizAreas,
  numExpuestosSuggestion,
  businessId,
  fila,
  canEdit = true,
}: {
  form: FilaFormState
  set: (field: 'peor_consecuencia' | 'area_id') => (e: {target: {value: string}}) => void
  setForm: (updater: (f: FilaFormState) => FilaFormState) => void
  idPrefix: string
  testIdPrefix: string
  matrizAreas: {id: string; name: string}[]
  numExpuestosSuggestion?: ReactNode
  businessId?: string
  fila?: Fila
  canEdit?: boolean
}) {
  const {t} = useTranslation()
  return (
    <TabsContent value="controles" className="space-y-4">
      {businessId && fila && (
        <section className="space-y-4">
          <GroupHeader
            label={t('ipevr.groups.controles')}
            help={t('ipevr.groups.controlesHelp')}
            testId={`${testIdPrefix}-help-controles`}
          />
          {CONTROL_TIPOS.map((tipo) => (
            <ControlDimensionEditor key={tipo} businessId={businessId} fila={fila} tipo={tipo} canEdit={canEdit} />
          ))}
        </section>
      )}

      {businessId && fila ? (
        <MedidasSection businessId={businessId} fila={fila} canEdit={canEdit} />
      ) : (
        <section
          className="rounded-lg border border-dashed border-border p-4"
          data-testid={`${testIdPrefix}-controles-medidas-note`}>
          <Hint>{t('ipevr.controles.addFilaNote')}</Hint>
        </section>
      )}

      <section className="space-y-4 border-t border-border pt-4">
        <GroupHeader
          label={t('ipevr.groups.criterios')}
          help={t('ipevr.groups.criteriosHelp')}
          testId={`${testIdPrefix}-help-criterios`}
        />
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-area`}>{t('ipevr.grid.area')}</Label>
          <EntitySearchSelect
            id={`${idPrefix}-area`}
            items={matrizAreas}
            value={matrizAreas.find((a) => a.id === form.area_id) ?? null}
            getKey={(a) => a.id}
            filterText={(a) => a.name}
            renderOption={(a) => a.name}
            renderTriggerValue={(a) => a.name}
            placeholder={t('ipevr.grid.noArea')}
            searchPlaceholder={t('ipevr.grid.areaSearchPlaceholder')}
            noResultsLabel={t('ipevr.grid.areaNoResults')}
            clearLabel={t('ipevr.grid.noArea')}
            testId={`${testIdPrefix}-area`}
            onPick={(a) => setForm((f) => ({...f, area_id: a?.id ?? ''}))}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-peor-consecuencia`}>{t('ipevr.grid.peorConsecuencia')}</Label>
          <Textarea
            maxLength={tope('TEXTO')}
            id={`${idPrefix}-peor-consecuencia`}
            minRows={2}
            value={form.peor_consecuencia}
            onChange={set('peor_consecuencia')}
            data-testid={`${testIdPrefix}-peor-consecuencia`}
          />
        </div>
        <label
          className="flex min-h-11 items-center gap-2.5 text-sm text-foreground"
          htmlFor={`${idPrefix}-requisito-legal`}>
          <Checkbox
            id={`${idPrefix}-requisito-legal`}
            checked={form.requisito_legal}
            onCheckedChange={(checked) => setForm((f) => ({...f, requisito_legal: checked}))}
            data-testid={`${testIdPrefix}-requisito-legal`}
          />
          {t('ipevr.grid.requisitoLegal')}
        </label>
      </section>
      {numExpuestosSuggestion}
    </TabsContent>
  )
}

function Gtc45Select({
  field,
  form,
  set,
  idPrefix,
  testIdPrefix,
  plainLabelKey,
  siglaLabel,
  options,
}: {
  field: 'nd' | 'ne' | 'nc'
  form: FilaFormState
  set: (field: 'nd' | 'ne' | 'nc') => (e: {target: {value: string}}) => void
  idPrefix: string
  testIdPrefix: string
  plainLabelKey: string
  siglaLabel: string
  options: Gtc45Option[]
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-${field}`}>
        {t(plainLabelKey)} ({siglaLabel})
      </Label>
      <select
        id={`${idPrefix}-${field}`}
        value={form[field]}
        onChange={set(field)}
        className={SELECT_CLASS}
        data-testid={`${testIdPrefix}-${field}`}>
        <option value="">{t('ipevr.grid.selectPlaceholder')}</option>
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export function EvaluacionTabContent({
  form,
  set,
  idPrefix,
  testIdPrefix,
  ndOptions,
  neOptions,
  ncOptions,
  numExpuestosSuggestion,
}: {
  form: FilaFormState
  set: (field: 'nd' | 'ne' | 'nc' | 'num_expuestos') => (e: {target: {value: string}}) => void
  idPrefix: string
  testIdPrefix: string
  ndOptions: Gtc45Option[]
  neOptions: Gtc45Option[]
  ncOptions: Gtc45Option[]
  numExpuestosSuggestion?: ReactNode
}) {
  const {t} = useTranslation()
  return (
    <TabsContent value="evaluacion" className="space-y-5">
      <GroupHeader
        label={t('ipevr.groups.evaluacion')}
        help={t('ipevr.groups.evaluacionHelp')}
        testId={`${testIdPrefix}-help-evaluacion`}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Gtc45Select
          field="nd"
          form={form}
          set={set}
          idPrefix={idPrefix}
          testIdPrefix={testIdPrefix}
          plainLabelKey="ipevr.grid.ndPlain"
          siglaLabel="ND"
          options={ndOptions}
        />
        <Gtc45Select
          field="ne"
          form={form}
          set={set}
          idPrefix={idPrefix}
          testIdPrefix={testIdPrefix}
          plainLabelKey="ipevr.grid.nePlain"
          siglaLabel="NE"
          options={neOptions}
        />
        <Gtc45Select
          field="nc"
          form={form}
          set={set}
          idPrefix={idPrefix}
          testIdPrefix={testIdPrefix}
          plainLabelKey="ipevr.grid.ncPlain"
          siglaLabel="NC"
          options={ncOptions}
        />
      </div>
      <div className="space-y-1.5 sm:max-w-xs">
        <Label htmlFor={`${idPrefix}-num-expuestos`}>{t('ipevr.grid.numExpuestos')}</Label>
        <Input
          maxLength={tope('NUM')}
          id={`${idPrefix}-num-expuestos`}
          inputMode="numeric"
          className="tabular-nums"
          value={form.num_expuestos}
          onChange={set('num_expuestos')}
          data-testid={`${testIdPrefix}-num-expuestos`}
        />
        {numExpuestosSuggestion}
      </div>
    </TabsContent>
  )
}
